import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bell, LogOut, Menu, Moon, Sun, UserRound, Users } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../i18n/I18nContext';
import { usePermissions } from '../../hooks/usePermissions';
import { useCourseTitle } from '../../hooks/useCourseTitle';
import { usePageLabel } from '../../hooks/usePageTitle';
import { formatLongDate } from '../../utils/datetime';
import LogoutDialog from '../LogoutDialog/LogoutDialog';
import LanguageSwitch from '../LanguageSwitch/LanguageSwitch';
import { BrandLogo, ShellAvatar } from '../Sidebar/SidebarFrame';
import { useIsCompact, useSidebarOpen, useTheme } from '../Layout/shellState';
import './TopBar.css';

const PRODUCT_NAME = 'Darsly CRM';

// Route → { parent?: { key, to }, title }. Keys resolve through t(). The first matching entry
// wins, so deeper routes come before their parents. A route with a `parent` is "nested": the bar
// shows a breadcrumb for it. Every other route shows today's date. The title is also what the
// phone pill falls back to when a page has no heading of its own.
const ROUTES = [
  { match: /^\/dashboard/, title: 'topbar.title.home' },
  { match: /^\/courses\/create/, parent: { key: 'nav.courses', to: '/courses' }, title: 'topbar.title.createCourse' },
  { match: /^\/courses\/(edit|[^/]+)/, parent: { key: 'nav.courses', to: '/courses' }, title: 'topbar.title.courseWorkspace' },
  { match: /^\/courses$/, title: 'topbar.title.courses' },
  { match: /^\/students\/[^/]+/, parent: { key: 'nav.students', to: '/students' }, title: 'topbar.title.studentDetail' },
  { match: /^\/mentor\/students\/[^/]+/, parent: { key: 'nav.students', to: '/students' }, title: 'topbar.title.studentDetail' },
  { match: /^\/students/, title: 'topbar.title.students' },
  { match: /^\/clients\/[^/]+/, parent: { key: 'nav.clients', to: '/clients' }, title: 'topbar.title.clientDetail' },
  { match: /^\/clients/, title: 'topbar.title.clients' },
  { match: /^\/sales/, title: 'topbar.title.sales' },
  { match: /^\/leads\/[^/]+/, parent: { key: 'nav.leads', to: '/leads' }, title: 'nav.leads' },
  { match: /^\/leads/, title: 'nav.leads' },
  { match: /^\/reminders\/[^/]+/, parent: { key: 'nav.reminders', to: '/reminders?tab=history' }, title: 'nav.reminders' },
  { match: /^\/reminders/, title: 'nav.reminders' },
  { match: /^\/promocodes/, title: 'nav.promocodes' },
  { match: /^\/inbox/, title: 'topbar.title.inbox' },
  { match: /^\/support/, title: 'layout.nav.support' },
  { match: /^\/statistics/, title: 'topbar.title.analytics' },
  { match: /^\/calendar/, title: 'topbar.title.calendar' },
  { match: /^\/team/, title: 'topbar.title.team' },
  { match: /^\/(settings|mentor\/profile)/, title: 'topbar.title.settings' },
  { match: /^\/mentors|^\/directory/, title: 'topbar.title.mentors' },
  // Teacher workspace
  { match: /^\/teacher\/dashboard/, title: 'layout.nav.myStudents' },
  { match: /^\/teacher\/students\/[^/]+/, parent: { key: 'layout.nav.myStudents', to: '/teacher/dashboard' }, title: 'topbar.title.studentDetail' },
  { match: /^\/teacher\/quiz-feedback/, parent: { key: 'layout.nav.myStudents', to: '/teacher/dashboard' }, title: 'topbar.title.quizFeedback' },
  { match: /^\/teacher\/at-risk/, title: 'layout.nav.atRisk' },
  { match: /^\/teacher\/metrics/, title: 'layout.nav.metrics' },
  { match: /^\/teacher\/(qa|courses)/, title: 'layout.nav.qa' },
];

// "/courses/edit/<id>" is a course's workspace; "/courses/<id>/<page>" one of its standalone
// sub-pages (quiz results, student progress, drop-off, Q&A, ...).
function courseFromPath(pathname) {
  const workspace = pathname.match(/^\/courses\/edit\/([^/]+)/);
  if (workspace) return { id: workspace[1], subPage: false };
  const sub = pathname.match(/^\/courses\/([^/]+)\/[^/]/);
  if (sub && sub[1] !== 'edit' && sub[1] !== 'create') return { id: sub[1], subPage: true };
  return null;
}

/**
 * Phone pill: the Darsly logo while the page's own heading is on screen, the compact route title
 * once that heading has scrolled up under the bar (the iOS "large title" behaviour). Pages with no
 * heading at all show the title straight away. The page scrolls inside .content-area, so that is
 * the observer's root.
 */
function useCompactTitle(pathname, enabled) {
  const [state, setState] = useState({ on: false, text: '' });
  useEffect(() => {
    setState({ on: false, text: '' });
    if (!enabled || typeof IntersectionObserver === 'undefined') return undefined;
    const scroller = document.querySelector('.layout .content-area');
    if (!scroller) return undefined;

    const update = (next) => setState((prev) => (prev.on === next.on && prev.text === next.text ? prev : next));
    let observer = null;
    let observed; // undefined until the first look, so "no heading" is a result too
    let frame = 0;
    const attach = () => {
      frame = 0;
      const heading = scroller.querySelector('h1');
      if (heading === observed) return;
      if (observer) observer.disconnect();
      observer = null;
      observed = heading;
      if (!heading) {
        update({ on: true, text: '' });
        return;
      }
      observer = new IntersectionObserver(([entry]) => {
        const top = entry.rootBounds ? entry.rootBounds.top : 0;
        update({ on: !entry.isIntersecting && entry.boundingClientRect.top < top, text: heading.textContent.trim() });
      }, { root: scroller, threshold: 0 });
      observer.observe(heading);
    };
    attach();
    // Pages draw their heading once their data is in: look again (at most once a frame).
    const mutations = new MutationObserver(() => { if (!frame) frame = requestAnimationFrame(attach); });
    mutations.observe(scroller, { childList: true, subtree: true });
    return () => {
      if (observer) observer.disconnect();
      mutations.disconnect();
      if (frame) cancelAnimationFrame(frame);
    };
  }, [pathname, enabled]);
  return state;
}

function TopBar({ onMenuOpen, notificationCount = 0 }) {
  const { user, logout } = useAuth();
  const { isAdmin, canSeeNav } = usePermissions();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { lang, t } = useI18n();
  const { isDark, toggle: toggleTheme } = useTheme();
  const [railOpen] = useSidebarOpen();
  const compactShell = useIsCompact();
  const compactTitle = useCompactTitle(pathname, compactShell);

  const route = ROUTES.find((r) => r.match.test(pathname));
  const course = courseFromPath(pathname);
  const courseTitle = useCourseTitle(course ? course.id : null);
  const pageLabel = usePageLabel(pathname);
  const isTeacherArea = pathname.startsWith('/teacher');
  const homePath = isTeacherArea ? '/teacher/dashboard' : '/dashboard';

  // Single-open menu manager: 'notif' | 'profile' | null.
  const [openMenu, setOpenMenu] = useState(null);
  const [showLogout, setShowLogout] = useState(false);
  const notifRef = useRef(null);
  const profileRef = useRef(null);
  const avatarBtnRef = useRef(null);
  useEffect(() => {
    if (!openMenu) return undefined;
    const holder = openMenu === 'notif' ? notifRef : profileRef;
    const onPointerDown = (e) => {
      if (holder.current && !holder.current.contains(e.target)) setOpenMenu(null);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      setOpenMenu(null);
      const trigger = holder.current && holder.current.querySelector('button[aria-haspopup]');
      if (trigger) trigger.focus();
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    // Open the profile menu with its first item under the keyboard.
    if (openMenu === 'profile' && holder.current) {
      const first = holder.current.querySelector('[role="menuitem"]');
      if (first) first.focus({ preventScroll: true });
    }
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenu]);

  const toggle = (name) => setOpenMenu((cur) => (cur === name ? null : name));
  const go = (path) => { setOpenMenu(null); navigate(path); };
  // The menu item that opened the dialog is gone by then: focus goes back to the avatar button.
  const closeLogout = () => {
    setShowLogout(false);
    if (avatarBtnRef.current) avatarBtnRef.current.focus({ preventScroll: true });
  };

  // Arrow keys move through the menu's items; Tab leaves it.
  const onMenuKeyDown = (e) => {
    if (e.key === 'Tab') { setOpenMenu(null); return; }
    const items = Array.from(e.currentTarget.querySelectorAll('[role="menuitem"]'));
    if (!items.length) return;
    const at = items.indexOf(document.activeElement);
    let next = null;
    if (e.key === 'ArrowDown') next = items[(at + 1) % items.length];
    else if (e.key === 'ArrowUp') next = items[(at - 1 + items.length) % items.length];
    else if (e.key === 'Home') next = items[0];
    else if (e.key === 'End') next = items[items.length - 1];
    if (next) { e.preventDefault(); next.focus(); }
  };

  const displayName = user?.name || user?.email?.split('@')[0] || t('layout.userFallback');
  const title = route ? t(route.title) : t('nav.home');

  // Nested routes: the ancestors as links, then the record the page shows ("Jane Karimova",
  // "Test Corp", the course title) once the page has named it, else the generic route title.
  // A course sub-page ends in its course: the page's own heading says which view it is.
  let trail = null;
  let current = null;
  if (course) {
    const courseName = courseTitle || t('topbar.title.courseWorkspace');
    trail = [{ label: t('nav.courses'), to: '/courses' }];
    if (course.subPage) trail.push({ label: courseName, to: `/courses/edit/${course.id}` });
    else current = courseName;
  } else if (route && route.parent) {
    trail = [{ label: t(route.parent.key), to: route.parent.to }];
    current = pageLabel || title;
  }

  // The browser tab names the page, in the user's language ("O'quvchilar · Darsly CRM"); a route
  // the table does not know keeps the plain product name. A page that knows better (a student's
  // name, an organisation's) sets its own title once it has loaded: the bar then leaves it alone
  // until the route changes, so a language switch does not turn "Jane Karimova" back into
  // "Student detail". Leaving the shell (sign-out) resets it.
  const tabTitle = route ? `${(course && courseTitle) || title} · ${PRODUCT_NAME}` : PRODUCT_NAME;
  const titleSetByBar = useRef(null);
  useEffect(() => {
    const mine = titleSetByBar.current;
    if (mine === null || document.title === mine || document.title === PRODUCT_NAME) {
      document.title = tabTitle;
      titleSetByBar.current = tabTitle;
    }
  }, [tabTitle]);
  useEffect(() => () => { document.title = PRODUCT_NAME; }, []);
  const count = Number(notificationCount) > 0 ? Number(notificationCount) : 0;

  return (
    <header className="topbar" role="banner">
      <div className="tb-left">
        <button
          type="button"
          className="tb-icon-btn tb-menu-btn"
          onClick={onMenuOpen}
          aria-label={t('nav.openMenu')}
        >
          <Menu size={22} aria-hidden="true" />
        </button>

        {/* The wordmark: on the phone pill always (until the compact title takes over), on
            desktop only when the rail is closed and no longer shows it. */}
        <Link
          to={homePath}
          className={`tb-brand${railOpen ? ' is-rail-open' : ''}${compactTitle.on ? ' is-swapped' : ''}`}
          aria-label="Darsly CRM"
        >
          <BrandLogo height={26} decorative />
        </Link>
        <span className={`tb-brand-rule${railOpen ? ' is-rail-open' : ''}`} aria-hidden="true" />
        {compactTitle.on && <span className="tb-compact-title">{compactTitle.text || title}</span>}

        {/* No page title here on purpose: the route's own <h1> lives in the page, and repeating
            it in the bar printed every title twice. Nested routes get a breadcrumb back to their
            parent; every other route shows today's date. */}
        {trail ? (
          <nav className="tb-crumbs" aria-label={t('layout.breadcrumb')}>
            {trail.map((crumb, index) => (
              <React.Fragment key={crumb.to}>
                {index > 0 && <span className="tb-crumb-sep" aria-hidden="true">/</span>}
                <Link to={crumb.to} className="tb-crumb">{crumb.label}</Link>
              </React.Fragment>
            ))}
            {current && (
              <>
                <span className="tb-crumb-sep" aria-hidden="true">/</span>
                <span className="tb-crumb-current" aria-current="page">{current}</span>
              </>
            )}
          </nav>
        ) : (
          <p className="tb-date">{formatLongDate(new Date(), lang)}</p>
        )}
      </div>

      <div className="tb-right">
        {/* Language: UZ · EN · RU, one segmented pill (the phone drawer carries its own). */}
        <LanguageSwitch className="tb-lang" buttonClassName="tb-lang-btn" />

        <button
          type="button"
          className="tb-icon-btn tb-theme"
          onClick={toggleTheme}
          title={t('topbar.theme')}
          aria-label={t('topbar.theme')}
          aria-pressed={isDark}
        >
          {isDark ? <Sun size={20} aria-hidden="true" /> : <Moon size={20} aria-hidden="true" />}
        </button>

        <div className="tb-notif" ref={notifRef}>
          <button
            type="button"
            className="tb-icon-btn tb-bell"
            onClick={() => toggle('notif')}
            aria-haspopup="dialog"
            aria-expanded={openMenu === 'notif'}
            title={t('topbar.notifications')}
            aria-label={count > 0 ? `${t('topbar.notifications')}, ${t('layout.unread', { count })}` : t('topbar.notifications')}
          >
            <Bell size={20} aria-hidden="true" />
            {count > 0 && <span className="tb-badge ui-count ui-count--danger" aria-hidden="true">{count > 99 ? '99+' : count}</span>}
          </button>
          {openMenu === 'notif' && (
            <div className="tb-panel tb-notif-panel" role="dialog" aria-label={t('topbar.notifications')}>
              <p className="tb-panel-title">{t('topbar.notifications')}</p>
              <div className="tb-notif-empty">
                <span className="tb-notif-empty-icon" aria-hidden="true"><Bell size={20} /></span>
                <p>{t('topbar.noNotifications')}</p>
              </div>
              {canSeeNav('inbox') && !isTeacherArea && (
                <button type="button" className="tb-panel-foot" onClick={() => go('/inbox')}>
                  {t('nav.inbox')}
                </button>
              )}
            </div>
          )}
        </div>

        <div className="tb-profile" ref={profileRef}>
          <button
            ref={avatarBtnRef}
            type="button"
            className={`tb-avatar-btn${openMenu === 'profile' ? ' is-open' : ''}`}
            onClick={() => toggle('profile')}
            aria-haspopup="menu"
            aria-expanded={openMenu === 'profile'}
            aria-label={t('layout.profileMenu')}
          >
            <ShellAvatar name={displayName} src={user?.avatar} size={36} presence />
          </button>

          {openMenu === 'profile' && (
            <div className="tb-panel tb-profile-panel" role="menu" aria-label={t('layout.profileMenu')} onKeyDown={onMenuKeyDown}>
              <div className="tb-profile-head">
                <ShellAvatar name={displayName} src={user?.avatar} size={44} />
                <div className="tb-profile-head-meta">
                  <p className="tb-profile-head-name">{displayName}</p>
                  {user?.email && <p className="tb-profile-head-email">{user.email}</p>}
                </div>
              </div>
              {!isTeacherArea && (
                <button type="button" role="menuitem" className="tb-menu-item" onClick={() => go('/settings/mentor')}>
                  <span className="tb-menu-icon" aria-hidden="true"><UserRound size={16} /></span>
                  {t('topbar.profile')}
                </button>
              )}
              {!isTeacherArea && isAdmin() && (
                <button type="button" role="menuitem" className="tb-menu-item" onClick={() => go('/team')}>
                  <span className="tb-menu-icon" aria-hidden="true"><Users size={16} /></span>
                  {t('nav.team')}
                </button>
              )}
              <button
                type="button"
                role="menuitem"
                className="tb-menu-item tb-menu-item--danger"
                onClick={() => { setOpenMenu(null); setShowLogout(true); }}
              >
                <span className="tb-menu-icon" aria-hidden="true"><LogOut size={16} /></span>
                {t('nav.logout')}
              </button>
            </div>
          )}
        </div>
      </div>

      <LogoutDialog open={showLogout} onClose={closeLogout} onConfirm={logout} />
    </header>
  );
}

export default TopBar;
